// src/components/admin/page-sections/ContainerSection.js
import React from 'react';
import PageSection from '../page-management/PageSection';

const ContainerSection = ({ content, children }) => {
  // Debug output
  console.log('ContainerSection rendering with:');
  console.log('Content:', content);
  console.log('Children:', children);
  
  const {
    containerType = 'standard',
    backgroundColor,
    padding = '1rem',
    margin,
    width,
    maxWidth,
    height,
    minHeight,
    
    // Flex specific props
    flexDirection = 'row',
    flexWrap = 'wrap',
    justifyContent = 'flex-start',
    alignItems = 'stretch',
    
    // Grid specific props
    gridColumns = '1fr 1fr 1fr',
    gridRows = 'auto',
    gridGap = '1rem',
    gridTemplateAreas
  } = content || {};

  // Convert container type to CSS
  const getContainerStyles = () => {
    const baseStyles = {
      backgroundColor,
      padding,
      margin,
      width: width || '100%',
      maxWidth,
      height,
      minHeight,
      boxSizing: 'border-box'
    };

    if (containerType === 'flex') {
      return {
        ...baseStyles,
        display: 'flex',
        flexDirection,
        flexWrap,
        justifyContent,
        alignItems
      };
    } else if (containerType === 'grid') {
      return {
        ...baseStyles,
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gridTemplateRows: gridRows,
        gap: gridGap,
        gridTemplateAreas: gridTemplateAreas || undefined
      };
    }

    // Default to standard div with block display
    return {
      ...baseStyles,
      display: 'block'
    };
  };

  // Get child-specific styles for positioning within container
  const getChildStyles = (childSection) => {
    if (!childSection || !childSection.content) return {};
    
    const childStyles = {};
    const childContent = childSection.content;
    
    // Grid-specific properties
    if (containerType === 'grid') {
      if (childContent.gridArea) childStyles.gridArea = childContent.gridArea;
      if (childContent.gridColumn) childStyles.gridColumn = childContent.gridColumn;
      if (childContent.gridRow) childStyles.gridRow = childContent.gridRow;
    }
    
    // Flex-specific properties
    if (containerType === 'flex') {
      if (childContent.flexGrow !== undefined) childStyles.flexGrow = childContent.flexGrow;
      if (childContent.flexShrink !== undefined) childStyles.flexShrink = childContent.flexShrink;
      if (childContent.flexBasis !== undefined) childStyles.flexBasis = childContent.flexBasis;
      if (childContent.alignSelf) childStyles.alignSelf = childContent.alignSelf;
    }
    
    // Common properties
    if (childContent.margin) childStyles.margin = childContent.margin;
    if (childContent.width) childStyles.width = childContent.width;
    
    return childStyles;
  };

  // Apply default grid/flex configuration for children if not specified
  const applyDefaultContainerLayout = () => {
    if (!children || children.length === 0) return [];
    
    const enhancedChildren = [...children];
    
    if (containerType === 'grid') {
      // For grid: ensure each child has a grid column if not specified
      enhancedChildren.forEach((child, index) => {
        // If grid has 3 columns by default, place each child in its own column
        // Fallback to span 1 if too many children
        if (!child.content.gridColumn && !child.content.gridArea) {
          const columnCount = gridColumns.split(' ').length;
          const column = (index % columnCount) + 1;
          child.content = {
            ...child.content,
            gridColumn: `${column} / span 1`
          };
        }
      });
    } else if (containerType === 'flex') {
      // For flex: give each child some default flex properties if not specified
      enhancedChildren.forEach(child => {
        if (!child.content.flexBasis && !child.content.flexGrow) {
          child.content = {
            ...child.content,
            flexGrow: 1,
            flexBasis: '0'
          };
        }
      });
    }
    
    return enhancedChildren;
  };

  // Process children to enhance with default layout properties if needed
  const processedChildren = applyDefaultContainerLayout();
  
  // If there are no children, render a placeholder
  if (!children || children.length === 0) {
    return (
      <div className="container-section" style={getContainerStyles()}>
        <div className="text-center py-4 text-gray-400">
          Empty Container - Add child sections
        </div>
      </div>
    );
  }
  
  // Display container style info for debugging
  const containerStyles = getContainerStyles();
  console.log('Container styles:', containerStyles);
  
  return (
    <div className="container-section" style={containerStyles}>
      {/* Render each child section with their specific grid/flex properties */}
      {processedChildren.map((childSection, index) => {
        const childStyles = getChildStyles(childSection);
        console.log(`Child ${index} styles:`, childStyles);
        
        return (
          <div 
            key={childSection._id || index} 
            className="container-child"
            style={childStyles}
          >
            <PageSection section={childSection} />
          </div>
        );
      })}
    </div>
  );
};

export default ContainerSection;



